// app/components/ui/Inventory.tsx
"use client";
import React from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown';
import Toast from '@little-tavern/shared/src/ui/toast';
import { useParams, useRouter } from 'next/navigation';
import { sendEvent } from '@little-tavern/shared/src/utils/analytics';

export type InventoryType = {
    amount: string;
    total_amount: string;
    display_fee: string;
    desc: string;
    recharge_product_id: string;
    corner_tip: string
    title: string
    display_price: string
    display_price_cny?: string
    corner_title: string
    promotion_price_desc: string
    original_price_desc: string
    promotion_desc: string
    remarks: string
    button_text: string
    feature_list: string[],
    price: number
    reward_amount: number
    supported_pay_types: string[]
    wechat_check_vpn?: boolean
    alipay_check_vpn?: boolean
    // 跳转地址
    target_url?: string
    // 积分类套餐专有：按特权等级额外赠送的积分数、含赠送的到账积分
    bonus_credits?: number
    total_credits?: number
    // 钻石类套餐：按特权等级额外赠送的钻石数（total_amount 已含）
    bonus_amount?: number
    // 赠送钻石折算的百分比（后端按用户特权等级算好），>0 时角标展示 +X%
    bonus_percent?: number
    // 到账数量后缀单位（后端下发，替代前端硬编码 💎）
    total_unit_label?: string
    amount_unit?: string
    reward_unit?: string
    // 支付页隐藏标题（后台配置），true 时卡片不渲染标题
    hide_title?: boolean
};

type InventoryProps = {
    inventorys: InventoryType[];
    select: (index: number) => void,
    selectedInventoryIndex: number
    rechargeData: any
    // 积分充值页：卡片展示积分数与赠送角标，而非 💎
    creditsMode?: boolean
    // 旧钻石充值页 /pay 的新版展示规则：角标只展示一个，后台配的 corner_title/corner_tip 优先，
    // 没配才展示等级赠送比例 +X%；且不再单独展示「额外赠送 N 💎」（已含在到账数量里）
    bonusRatioLayout?: boolean
};
const Inventory = ({ inventorys, select, selectedInventoryIndex, rechargeData, creditsMode = false, bonusRatioLayout = false }: InventoryProps) => {
    const { t } = useTranslation()
    const params = useParams()
    const lang = Array.isArray(params.lang) ? params.lang[0] : params.lang
    const isSimplifiedChinese = lang === 'zh'
    const router = useRouter()
    const onSelect = (index: number, isDisabled: boolean, disabledMsg: string) => {
        // 套餐选择打点
        const inventory = inventorys[index];
        sendEvent('package_select', {
            event_category: 'package',
            package_index: index,
            package_title: inventory.title,
            recharge_product_id: inventory.recharge_product_id,
        });
        if (isDisabled) {
            disabledMsg && Toast.notify({ type: 'error', message: disabledMsg })
            return;
        }
        const targetUrl = inventorys[index].target_url;
        if (targetUrl) {
            router.push(targetUrl);
        } else {
            select(index)
        }
    }
    return (<div className='pt-1 md:pb-4 '>
        {rechargeData?.banner_tip && <div className='text-center'><div className='bg-red-500 rounded p-0.5 text-white inline-block mx-auto text-sm mb-2.5 px-3'>{rechargeData?.banner_tip}</div></div>}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {
                inventorys?.length > 0 && inventorys?.map((inventory: any, index: number) => {
                    // 角标只展示一组，优先级：后台配置的 corner_title/corner_tip > 等级赠送比例 +X% > 不展示
                    const bonusPercent = Number(inventory.bonus_percent) || 0
                    const cornerNodes = (inventory.corner_title || inventory.corner_tip)
                        ? <>
                            {inventory.corner_title && <div className='text-xs bg-red-500 rounded p-0.5 text-white'>{inventory.corner_title}</div>}
                            {inventory.corner_tip && <p className='text-xs text-yellow-500 mr-0.5'>{inventory.corner_tip}</p>}
                        </>
                        : bonusPercent > 0
                            ? <div className='text-xs bg-red-500 rounded p-0.5 text-white'>{t('app.pay.bonus_percent_corner', { n: bonusPercent })}</div>
                            : null
                    // 后台配置「支付页隐藏标题」时不渲染标题（角标不受影响）
                    const displayTitle = inventory.hide_title ? '' : inventory.title
                    return <div key={inventory.recharge_product_id} className={cn('w-full rounded-md px-1 border dark:border-gray-500 dark:bg-gray-900 bg-white relative cursor-pointer', bonusRatioLayout ? 'flex flex-col pt-1.5 pb-1.5' : 'pt-1 pb-2', selectedInventoryIndex === index && 'border-1 !border-purple-500 dark:!bg-gray-800', inventory.disabled && 'opacity-50', inventory.target_url && '')} onClick={() => onSelect(index, inventory.disabled, inventory.disabled_message)}>
                        {bonusRatioLayout
                            // 新版布局：标题与角标同一行顶端对齐，行高由较高的一侧决定，卡片不再需要顶部留白补偿；
                            // 两者都为空时整行不渲染，卡片自然收缩变矮
                            ? (displayTitle || cornerNodes) && <div className='flex items-start justify-between'>
                                {/* flex-1 + min-w-0 让标题吃掉角标之外的全部宽度并按实际可用空间截断 */}
                                <div className='flex-1 min-w-0 text-sm text-left truncate dark:text-gray-200'>{displayTitle}</div>
                                {/* 负 margin 抵消卡片 px-1(4px)+pt-1.5(6px)+边框(1px)，角标位置与改动前的 -right-[1px] -top-[1px] 一致 */}
                                {cornerNodes && <div className='flex flex-col items-end text-right shrink-0 ml-1 -mr-[6px] -mt-[8px]'>{cornerNodes}</div>}
                            </div>
                            : <>
                                {cornerNodes && <div className='absolute flex flex-col items-end -right-[1px] -top-[1px] text-right'>{cornerNodes}</div>}
                                <div className='text-sm dark:text-gray-200'>{displayTitle}</div>
                            </>}
                        {/* 新版布局：内容在标题/角标那一行之外的剩余空间垂直居中，混排行里不会与相邻卡片错位 */}
                        <div className={cn('text-center', bonusRatioLayout ? 'flex-1 flex flex-col justify-center' : 'mt-3')}>
                            <div className={cn(!bonusRatioLayout && 'mt-1 mb-2')}>
                                <div className=''>
                                    {creditsMode
                                        ? <div className={cn('name dark:font-bold', bonusRatioLayout ? 'text-xl leading-tight' : 'text-2xl')}>{inventory.total_credits}<span className={cn('inline-block mx-1', bonusRatioLayout ? 'text-sm' : 'text-base')}>{t('app.pay.credits_unit')}</span></div>
                                        : inventory.total_amount !== 0 && <div className={cn('name dark:font-bold', bonusRatioLayout ? 'text-xl leading-tight' : 'text-2xl')}>{inventory.total_amount}<span className={cn('inline-block mx-1', bonusRatioLayout ? 'text-sm' : 'text-base')}>{inventory.total_unit_label || '💎'}</span></div>}

                                    <div className={cn('desc text-purple-500 dark:font-bold', bonusRatioLayout ? 'text-base leading-tight' : 'text-lg mt-2')}>
                                        {isSimplifiedChinese && inventory.display_price_cny
                                            ? <>{inventory.display_price_cny}元<span className={bonusRatioLayout ? 'text-[10px]' : 'text-xs'}>({inventory.display_price})</span></>
                                            : inventory.display_price}
                                    </div>
                                </div>
                            </div>
                            {creditsMode && inventory.bonus_credits > 0 && <div className='text-yellow-500 text-xs'>{t('app.pay.bonus_credits', { n: inventory.bonus_credits })}</div>}
                            {/* 赠送已加进到账数量，新版布局不再重复展示「额外赠送 N 💎」 */}
                            {!bonusRatioLayout && !creditsMode && inventory.bonus_amount > 0 && <div className='text-yellow-500 text-xs'>{t('app.pay.bonus_diamond', { n: inventory.bonus_amount })}</div>}
                            {!bonusRatioLayout && (
                                inventory.promotion_desc ? <div className='text-yellow-500 text-xs'>{inventory.promotion_desc}</div> : <div className='text-yellow-500 text-xs'>{inventory.desc}</div>
                            )}
                            {inventory.target_url && <div className='underline text-sm text-blue-500 inline-block mt-1.5'>{t('app.pay.link_btn')}</div>}
                        </div>
                    </div>
                })
            }
        </div>
        <div className='text-xs dark:text-gray-100 mt-1 text-left px-1 mt-2'>
            {rechargeData?.remarks?.length > 0 && rechargeData?.remarks?.map((remark: string, index: number) => {
                return <div key={index} className=''><ReactMarkdown components={{
                    li: ({ node, ...props }) => <li className='list-decimal ml-4' {...props} />,
                    a: ({ node, ...props }) => <a target="_blank" className='text-blue-500 underline' rel="" {...props} />
                }}>{remark}</ReactMarkdown></div>
            })}
        </div>
    </div>
    );
};

export default Inventory;
